/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useQueries } from '@tanstack/react-query';
import clsx from 'clsx';
import Fuse from 'fuse.js';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { resolveURLToNavigation } from '@gitbook/app-navigation';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Collection,
    CollectionExpandButton,
    DateShortRelative,
    Description,
    EmptyState,
    EmptyStateIcon,
    ExpandButton,
    Heading,
    IsolateField,
    LoadingIndicator,
    TextInput,
    Tree,
    TreeItem,
    TreeItemContent,
} from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state/api';
import { PageIcon } from '../Page';
import {
    createPageTree,
    isSelectablePage,
    type PageTreeItem as PageTreeNode,
} from './creation/pageTree';
import { SpaceIcon } from './SpaceIcon';

interface SpaceTreeItem {
    type: 'space';
    id: string;
    space: api.Space;
    pages: PageTreeNode[] | null;
    revision?: api.Revision;
    revisionError: boolean;
}

interface CollectionTreeItem {
    type: 'collection';
    id: string;
    title: string;
    children: SpaceTreeItem[];
}

type HierarchicalTreeItem = CollectionTreeItem | SpaceTreeItem;

export interface SpaceListSelectorProps {
    spaces: api.Space[];
    collectionNameMap: ReadonlyMap<string, string>;
    onChange: (item: api.Space) => void | Promise<void>;
    onPageChange?: (
        space: api.Space,
        page: api.RevisionPageDocument,
        revision: api.Revision
    ) => void;
    revisions?: ReadonlyMap<string, api.Revision>;
    autoFocus?: boolean;
    actionLabel: string;
    searchPlaceholder?: string;
    alwaysShowSearch?: boolean;
    bordered?: boolean;
}

export function SpaceListSelector({
    spaces,
    onChange,
    autoFocus,
    actionLabel,
    collectionNameMap,
    onPageChange,
    revisions,
    searchPlaceholder = 'Find space...',
    alwaysShowSearch = false,
    bordered = false,
}: SpaceListSelectorProps) {
    const sortedSpaces = React.useMemo(() => {
        return spaces.sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
    }, [spaces]);

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    const [searchQuery, setSearchQuery] = React.useState('');
    const [userExpandedKeys, setUserExpandedKeys] = React.useState<Set<string>>(new Set());

    const [requestedRevisionIds, setRequestedRevisionIds] = React.useState<Set<string>>(new Set());
    const requestedRevisionSpaces = React.useMemo(
        () =>
            onPageChange
                ? spaces.filter(
                      (space) => requestedRevisionIds.has(space.id) && !revisions?.has(space.id)
                  )
                : [],
        [onPageChange, requestedRevisionIds, revisions, spaces]
    );
    const revisionResults = useQueries({
        queries: requestedRevisionSpaces.map((space) =>
            apiQuery('getRevisionById', {
                spaceId: space.id,
                revisionId: space.revision,
                metadata: false,
                computed: true,
            })
        ),
    });
    const loadedRevisions = React.useMemo(() => {
        const result = new Map(revisions);
        revisionResults.forEach((query, index) => {
            if (query.data) {
                result.set(requestedRevisionSpaces[index]!.id, query.data);
            }
        });
        return result;
    }, [requestedRevisionSpaces, revisionResults, revisions]);
    const revisionErrors = React.useMemo(
        () =>
            new Set(
                revisionResults.flatMap((query, index) =>
                    query.isError ? [requestedRevisionSpaces[index]!.id] : []
                )
            ),
        [requestedRevisionSpaces, revisionResults]
    );

    // Create hierarchical tree structure
    const hierarchicalItems = React.useMemo(() => {
        return createHierarchicalSpaceTree(
            sortedSpaces,
            collectionNameMap,
            loadedRevisions,
            Boolean(onPageChange),
            revisionErrors
        );
    }, [sortedSpaces, collectionNameMap, loadedRevisions, onPageChange, revisionErrors]);

    const fuse = React.useMemo(() => {
        return new Fuse(sortedSpaces, {
            shouldSort: true,
            threshold: 0.6,
            distance: 100,
            minMatchCharLength: 1,
            keys: ['title'],
        });
    }, [sortedSpaces]);

    // Filter based on search query
    const filteredHierarchicalItems = React.useMemo(
        () => filterHierarchicalTree(hierarchicalItems, searchQuery, fuse),
        [hierarchicalItems, searchQuery, fuse]
    );

    const currentItems = searchQuery ? filteredHierarchicalItems : hierarchicalItems;

    // Auto-expand collections that have spaces (initial state)
    const autoExpandedKeys = React.useMemo(() => {
        return new Set(
            currentItems
                .filter(
                    (item): item is CollectionTreeItem =>
                        item.type === 'collection' && item.children.length > 0
                )
                .map((item) => item.id)
        );
    }, [currentItems]);

    // Determine final expanded keys based on user interaction
    const currentExpandedKeys = React.useMemo(() => {
        // During normal browsing, use user preferences but default to auto-expanded
        // If user hasn't interacted with a collection, it should be expanded by default
        const expanded = new Set(autoExpandedKeys);

        userExpandedKeys.forEach((key) => {
            if (autoExpandedKeys.has(key)) {
                expanded.delete(key);
            } else {
                expanded.add(key);
            }
        });

        return expanded;
    }, [autoExpandedKeys, userExpandedKeys]);

    const handleExpandedChange = (keys: Set<string>) => {
        if (onPageChange) {
            const spaceIds = new Set(spaces.map((space) => space.id));
            const next = new Set<string>();
            keys.forEach((key) => {
                if (spaceIds.has(key)) {
                    next.add(key);
                }
            });
            setRequestedRevisionIds(next);
        }

        const userToggled = new Set<string>();

        autoExpandedKeys.forEach((key) => {
            if (!keys.has(key)) {
                userToggled.add(key);
            }
        });

        keys.forEach((key) => {
            if (!autoExpandedKeys.has(key)) {
                userToggled.add(key);
            }
        });

        setUserExpandedKeys(userToggled);
    };

    return (
        <>
            {alwaysShowSearch || spaces.length > 7 ? (
                <IsolateField>
                    <TextInput
                        ref={inputRef}
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                        }}
                        leadingIcon={Icon.Search}
                        className="mb-2"
                        autoFocus={autoFocus}
                    />
                </IsolateField>
            ) : null}

            <div>
                <Tree
                    items={currentItems}
                    expandedKeys={currentExpandedKeys}
                    onExpandedChange={handleExpandedChange}
                    aria-label="Spaces"
                    className={clsx(
                        'flex h-fit max-h-80 flex-col gap-0.5 overflow-y-auto',
                        bordered ? 'pb-0' : 'border-0 px-0'
                    )}
                    renderEmptyState={() => {
                        return (
                            <EmptyState className="h-80">
                                <EmptyStateIcon>
                                    <Icon.Space />
                                </EmptyStateIcon>
                                <Heading>
                                    {searchQuery ? 'No space matching' : 'No spaces available'}
                                </Heading>
                                <Description>
                                    {searchQuery
                                        ? 'There are no spaces matching your search query.'
                                        : 'There are no spaces to display.'}
                                </Description>
                            </EmptyState>
                        );
                    }}
                >
                    {function renderTreeItem(item) {
                        switch (item.type) {
                            case 'space': {
                                return (
                                    <TreeItem
                                        textValue={item.space.title}
                                        className={clsx(
                                            'group/item relative cursor-default rounded-md transition-all hover:bg-standout-base'
                                        )}
                                    >
                                        <SpaceTreeItem
                                            space={item.space}
                                            pages={item.pages}
                                            actionLabel={actionLabel}
                                            onSelect={() => onChange(item.space)}
                                            onPageSelect={
                                                onPageChange && item.revision
                                                    ? (page) =>
                                                          onPageChange(
                                                              item.space,
                                                              page,
                                                              item.revision!
                                                          )
                                                    : undefined
                                            }
                                            revisionError={item.revisionError}
                                        />
                                    </TreeItem>
                                );
                            }
                            case 'collection': {
                                return (
                                    <TreeItem
                                        textValue={item.title}
                                        className={clsx(
                                            'group/tree-item relative cursor-default rounded-md transition-all'
                                        )}
                                    >
                                        <TreeItemContent>
                                            <CollectionExpandButton />
                                            <div className="flex-1 truncate text-left font-medium">
                                                {item.title}
                                            </div>
                                            <div className="text-sm text-muted">
                                                {item.children.length === 1 ? 'space' : 'spaces'}
                                            </div>
                                        </TreeItemContent>
                                        <Collection items={item.children}>
                                            {renderTreeItem}
                                        </Collection>
                                    </TreeItem>
                                );
                            }
                            default: {
                                assertNever(item);
                            }
                        }
                    }}
                </Tree>
            </div>
        </>
    );
}

function SpaceTreeItem({
    actionLabel,
    space,
    pages,
    onSelect,
    onPageSelect,
    revisionError,
}: {
    space: api.Space;
    pages: PageTreeNode[] | null;
    actionLabel: string;
    onSelect: () => void | Promise<void>;
    onPageSelect?: (page: api.RevisionPageDocument) => void;
    revisionError: boolean;
}) {
    const screen = resolveURLToNavigation(space.urls.app);
    assert(screen !== null, 'Space URL should resolve to a navigation screen');

    return (
        <>
            <TreeItemContent>
                {pages === null || pages.length > 0 ? <ExpandButton /> : null}
                <SpaceIcon space={space} size={20} />
                <div className="flex-1 truncate text-left">{space.title}</div>
                <div className="text-muted group-hover/item:invisible">
                    Last edited <DateShortRelative value={space.updatedAt} onlyDate={false} />
                </div>
                <div className="invisible absolute right-1.5 flex shrink-0 items-center gap-2 group-hover/item:visible">
                    <Button
                        href={{ screen }}
                        target="_blank"
                        label="View space"
                        trailingIcon={Icon.ArrowUpRight}
                        size="small"
                        kind="secondary"
                    />
                    <Button
                        label={actionLabel}
                        size="small"
                        kind="primary"
                        onPress={() => onSelect()}
                        loadable
                    />
                </div>
            </TreeItemContent>
            {(() => {
                if (pages === null) {
                    return (
                        <Collection
                            items={[
                                {
                                    id: `${space.id}:pages-loading`,
                                    revisionError,
                                },
                            ]}
                        >
                            {(item) => (
                                <TreeItem
                                    textValue={
                                        item.revisionError ? 'Pages unavailable' : 'Loading pages'
                                    }
                                >
                                    <TreeItemContent>
                                        <div className="w-7 shrink-0" />
                                        {item.revisionError ? (
                                            <span className="text-muted">Pages unavailable</span>
                                        ) : (
                                            <LoadingIndicator size="small" />
                                        )}
                                    </TreeItemContent>
                                </TreeItem>
                            )}
                        </Collection>
                    );
                }
                if (pages.length > 0) {
                    return (
                        <Collection items={pages}>
                            {(item) => <PageTreeItem item={item} onSelect={onPageSelect} />}
                        </Collection>
                    );
                }
                return null;
            })()}
        </>
    );
}

function PageTreeItem(props: {
    item: PageTreeNode;
    onSelect?: (page: api.RevisionPageDocument) => void;
}) {
    const { item, onSelect } = props;
    const selectable = isSelectablePage(item.page, item.hiddenByAncestor) && Boolean(onSelect);
    return (
        <TreeItem
            textValue={item.page.title}
            className={clsx('group/item relative rounded-md', selectable && 'cursor-default')}
        >
            <TreeItemContent>
                {/* Offsets the page icon to align with the space/group row's text, which starts after its own icon. */}
                <div className="w-7 shrink-0" />
                {item.children.length > 0 ? <ExpandButton /> : null}
                <PageIcon page={item.page} size={20} />
                <div className="flex-1 truncate text-left">{item.page.title}</div>
                {selectable ? (
                    <div className="invisible absolute right-1.5 flex shrink-0 items-center gap-2 group-hover/item:visible">
                        <Button
                            label="Use as content"
                            size="small"
                            kind="primary"
                            onPress={() => onSelect?.(item.page as api.RevisionPageDocument)}
                        />
                    </div>
                ) : null}
            </TreeItemContent>
            {item.children.length > 0 ? (
                <Collection items={item.children}>
                    {(child) => <PageTreeItem item={child} onSelect={onSelect} />}
                </Collection>
            ) : null}
        </TreeItem>
    );
}

function createHierarchicalSpaceTree(
    spaces: api.Space[],
    collectionNameMap: ReadonlyMap<string, string>,
    revisions: ReadonlyMap<string, api.Revision>,
    pageSelectionEnabled: boolean,
    revisionErrors: ReadonlySet<string>
): ReadonlyArray<HierarchicalTreeItem> {
    const result: HierarchicalTreeItem[] = [];

    // Group spaces by their parent collection
    const spacesByCollection = new Map<string | null, api.Space[]>();
    spaces.forEach((space) => {
        const parentId = space.parent ?? null;
        if (!spacesByCollection.has(parentId)) {
            spacesByCollection.set(parentId, []);
        }

        spacesByCollection.get(parentId)?.push(space);
    });

    // Add collections with their spaces
    spacesByCollection.forEach((spacesInCollection, collectionId) => {
        if (collectionId && spacesInCollection.length > 0) {
            result.push({
                id: collectionId,
                type: 'collection',
                title: collectionNameMap.get(collectionId) ?? `Collection ${collectionId}`,
                children: spacesInCollection.map((space) => ({
                    type: 'space',
                    id: space.id,
                    space,
                    pages: revisionsForSpace(space, revisions, pageSelectionEnabled),
                    revision: revisions.get(space.id),
                    revisionError: revisionErrors.has(space.id),
                })),
            });
        }
    });

    // Add root-level spaces
    const rootSpaces = spacesByCollection.get(null) ?? [];
    rootSpaces.forEach((space) => {
        result.push({
            type: 'space',
            id: space.id,
            space,
            pages: revisionsForSpace(space, revisions, pageSelectionEnabled),
            revision: revisions.get(space.id),
            revisionError: revisionErrors.has(space.id),
        });
    });

    return result;
}

function revisionsForSpace(
    space: api.Space,
    revisions: ReadonlyMap<string, api.Revision>,
    pageSelectionEnabled: boolean
): PageTreeNode[] | null {
    const revision = revisions.get(space.id);
    return revision ? createPageTree(revision, space.id) : pageSelectionEnabled ? null : [];
}

function filterHierarchicalTree(
    items: ReadonlyArray<HierarchicalTreeItem>,
    searchQuery: string,
    fuse: Fuse<api.Space>
): ReadonlyArray<HierarchicalTreeItem> {
    if (!searchQuery.trim()) {
        return items;
    }

    const spaceRanks = new Map(
        fuse.search(searchQuery).map(({ item }, rank) => [item.id, rank] as const)
    );
    const filteredItems: Array<{ item: HierarchicalTreeItem; rank: number }> = [];

    items.forEach((item) => {
        if (item.type === 'collection') {
            // Filter children spaces that match the search
            const matchingChildren = item.children
                .flatMap((child) => {
                    const rank = spaceRanks.get(child.space.id);
                    return rank === undefined ? [] : [{ child, rank }];
                })
                .sort((a, b) => a.rank - b.rank);

            // Include collection if it has matching children
            const bestChild = matchingChildren[0];
            if (bestChild) {
                filteredItems.push({
                    item: { ...item, children: matchingChildren.map(({ child }) => child) },
                    rank: bestChild.rank,
                });
            }
        } else {
            // Include root-level space if it matches
            const rank = spaceRanks.get(item.space.id);
            if (rank !== undefined) {
                filteredItems.push({ item, rank });
            }
        }
    });

    return filteredItems.sort((a, b) => a.rank - b.rank).map(({ item }) => item);
}
